import {
  AlignBottomIcon,
  ArrowBendDownLeftIcon,
  ArrowBendUpLeftIcon,
  ArrowCircleRightIcon,
  ArrowCircleUpIcon,
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  ArrowDownIcon,
  ArrowDownRightIcon,
  ArrowElbowUpRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUUpLeftIcon,
  ArrowUpIcon,
  ArrowUpLeftIcon,
  ArrowUpRightIcon,
  ArrowsClockwiseIcon,
  ArrowsDownUpIcon,
  ArrowsHorizontalIcon,
  ArrowsOutSimpleIcon,
  ArticleIcon,
  AsteriskIcon,
  AtIcon,
  BarbellIcon,
  type IconWeight as BaseIconWeight,
  BirdIcon,
  BookBookmarkIcon,
  BookmarkSimpleIcon,
  BuildingIcon,
  CalendarBlankIcon,
  CardholderIcon,
  CardsIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CaretUpIcon,
  CassetteTapeIcon,
  ChartBarIcon,
  ChartLineIcon,
  ChatCircleDotsIcon,
  ChatCircleIcon,
  ChatsCircleIcon,
  CheckCircleIcon,
  CheckIcon,
  CheckSquareIcon,
  ChecksIcon,
  CircleIcon,
  CirclesThreeIcon,
  ClockCountdownIcon,
  ClockCounterClockwiseIcon,
  CopyIcon,
  CrosshairSimpleIcon,
  CrownSimpleIcon,
  CurrencyCircleDollarIcon,
  DotsSixVerticalIcon,
  DotsThreeIcon,
  DotsThreeOutlineIcon,
  DownloadSimpleIcon,
  EarSlashIcon,
  ExamIcon,
  EyeIcon,
  EyeSlashIcon,
  FileIcon,
  FileImageIcon,
  FlagBannerFoldIcon,
  FlagIcon,
  FlowArrowIcon,
  FlowerTulipIcon,
  FolderSimpleDashedIcon,
  FunnelSimpleIcon,
  GearSixIcon,
  GlobeIcon,
  GooglePodcastsLogoIcon,
  GraduationCapIcon,
  GreaterThanOrEqualIcon,
  HandWavingIcon,
  HashIcon,
  HeadsetIcon,
  HouseIcon,
  type IconProps,
  IdentificationBadgeIcon,
  InfoIcon,
  KeyIcon,
  LessThanOrEqualIcon,
  LightningIcon,
  ListChecksIcon,
  ListIcon,
  ListNumbersIcon,
  LockSimpleIcon,
  MagnifyingGlassIcon,
  MagnifyingGlassPlusIcon,
  MapPinLineIcon,
  MedalIcon,
  MegaphoneSimpleIcon,
  MicrophoneIcon,
  MinusIcon,
  MonitorIcon,
  NavigationArrowIcon,
  NoteIcon,
  PaintBrushIcon,
  PaperPlaneTiltIcon,
  ParagraphIcon,
  PauseIcon,
  PencilLineIcon,
  PencilSimpleIcon,
  PhoneIcon,
  PhoneIncomingIcon,
  PhoneOutgoingIcon,
  PlayIcon,
  PlusIcon,
  PrinterIcon,
  ProhibitIcon,
  PulseIcon,
  QuestionIcon,
  RabbitIcon,
  RecordIcon,
  RocketLaunchIcon,
  SealIcon,
  ShareFatIcon,
  SlidersHorizontalIcon,
  SmileyAngryIcon,
  SmileyIcon,
  SmileyMehIcon,
  SneakerMoveIcon,
  SparkleIcon,
  SpeakerSimpleHighIcon,
  SpeedometerIcon,
  SquareIcon,
  SquareSplitHorizontalIcon,
  SquaresFourIcon,
  StackSimpleIcon,
  TagIcon,
  TextAaIcon,
  TextBIcon,
  TextItalicIcon,
  TextStrikethroughIcon,
  TextTIcon,
  TextUnderlineIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  TimerIcon,
  TrashSimpleIcon,
  TrayIcon,
  TrendDownIcon,
  TrendUpIcon,
  TriangleIcon,
  TrophyIcon,
  UserCheckIcon,
  UserCircleCheckIcon,
  UserCircleIcon,
  UserRectangleIcon,
  UserSoundIcon,
  UserSwitchIcon,
  UsersIcon,
  VinylRecordIcon,
  WarningCircleIcon,
  WarningDiamondIcon,
  XCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { Suspense, forwardRef, memo } from 'react'

interface Props extends Omit<IconProps, 'ref'> {
  name: IconTypes
  title?: string
}

export type IconTypes = keyof typeof iconMap
export type IconWeight = BaseIconWeight

export const Icon = memo(
  forwardRef<SVGSVGElement, Props>(({ name, weight = 'bold', ...props }, ref) => {
    const Icon = iconMap[name]
    if (!Icon) {
      console.error(`Icon not found: ${name}`)
      return null
    }
    const fallback = <div className={props.className} />
    return (
      <Suspense fallback={fallback}>
        <Icon
          ref={ref}
          className={props.className}
          size={props.size ?? 16}
          weight={weight}
          {...props}
        />
      </Suspense>
    )
  }),
)

const iconMap = {
  'align-bottom': AlignBottomIcon,
  'arrow-bend-down-left': ArrowBendDownLeftIcon,
  'arrow-bend-up-left': ArrowBendUpLeftIcon,
  'arrow-circle-right': ArrowCircleRightIcon,
  'arrow-circle-up': ArrowCircleUpIcon,
  'arrow-clockwise': ArrowClockwiseIcon,
  'arrow-counter-clockwise': ArrowCounterClockwiseIcon,
  'arrow-down': ArrowDownIcon,
  'arrow-down-right': ArrowDownRightIcon,
  'arrow-elbow-up-right': ArrowElbowUpRightIcon,
  'arrow-left': ArrowLeftIcon,
  'arrow-right': ArrowRightIcon,
  'arrow-u-up-left': ArrowUUpLeftIcon,
  'arrow-up': ArrowUpIcon,
  'arrow-up-left': ArrowUpLeftIcon,
  'arrow-up-right': ArrowUpRightIcon,
  'arrows-clockwise': ArrowsClockwiseIcon,
  'arrows-down-up': ArrowsDownUpIcon,
  'arrows-horizontal': ArrowsHorizontalIcon,
  'arrows-out-simple': ArrowsOutSimpleIcon,
  'article': ArticleIcon,
  'asterisk': AsteriskIcon,
  'at': AtIcon,
  'barbell': BarbellIcon,
  'bird': BirdIcon,
  'book-bookmark': BookBookmarkIcon,
  'bookmark-simple': BookmarkSimpleIcon,
  'building': BuildingIcon,
  'calendar': CalendarBlankIcon,
  'cardholder': CardholderIcon,
  'cards': CardsIcon,
  'caret-down': CaretDownIcon,
  'caret-left': CaretLeftIcon,
  'caret-right': CaretRightIcon,
  'caret-up': CaretUpIcon,
  'cassette-tape': CassetteTapeIcon,
  'chart-bar': ChartBarIcon,
  'chart-line': ChartLineIcon,
  'chat-circle': ChatCircleIcon,
  'chat-circle-dots': ChatCircleDotsIcon,
  'chats-circle': ChatsCircleIcon,
  'check': CheckIcon,
  'check-circle': CheckCircleIcon,
  'check-square': CheckSquareIcon,
  'checks': ChecksIcon,
  'circle': CircleIcon,
  'circles-three': CirclesThreeIcon,
  'clock-countdown': ClockCountdownIcon,
  'clock-counter-clockwise': ClockCounterClockwiseIcon,
  'copy': CopyIcon,
  'crosshair-simple': CrosshairSimpleIcon,
  'crown-simple': CrownSimpleIcon,
  'currency-circle-dollar': CurrencyCircleDollarIcon,
  'dots-six-vertical': DotsSixVerticalIcon,
  'dots-three': DotsThreeIcon,
  'dots-three-outline': DotsThreeOutlineIcon,
  'download-simple': DownloadSimpleIcon,
  'ear-slash': EarSlashIcon,
  'exam': ExamIcon,
  'eye': EyeIcon,
  'eye-slash': EyeSlashIcon,
  'file': FileIcon,
  'file-image': FileImageIcon,
  'flag': FlagIcon,
  'flag-banner-fold': FlagBannerFoldIcon,
  'flow-arrow': FlowArrowIcon,
  'flower-tulip': FlowerTulipIcon,
  'folder-simple-dashed': FolderSimpleDashedIcon,
  'funnel-simple': FunnelSimpleIcon,
  'gear-six': GearSixIcon,
  'globe': GlobeIcon,
  'google-podcasts-logo': GooglePodcastsLogoIcon,
  'graduation-cap': GraduationCapIcon,
  'greater-than-or-equal': GreaterThanOrEqualIcon,
  'hand-waving': HandWavingIcon,
  'hash': HashIcon,
  'headset': HeadsetIcon,
  'home': HouseIcon,
  'identification-badge': IdentificationBadgeIcon,
  'info': InfoIcon,
  'key': KeyIcon,
  'less-than-or-equal': LessThanOrEqualIcon,
  'lightning': LightningIcon,
  'list': ListIcon,
  'list-checks': ListChecksIcon,
  'list-numbers': ListNumbersIcon,
  'lock-simple': LockSimpleIcon,
  'magnifying-glass': MagnifyingGlassIcon,
  'magnifying-glass-plus': MagnifyingGlassPlusIcon,
  'map-pin-line': MapPinLineIcon,
  'medal': MedalIcon,
  'megaphone-simple': MegaphoneSimpleIcon,
  'microphone': MicrophoneIcon,
  'minus': MinusIcon,
  'monitor': MonitorIcon,
  'navigation-arrow': NavigationArrowIcon,
  'note': NoteIcon,
  'paint-brush': PaintBrushIcon,
  'paper-plane-tilt': PaperPlaneTiltIcon,
  'paragraph': ParagraphIcon,
  'pause': PauseIcon,
  'pencil-line': PencilLineIcon,
  'pencil-simple': PencilSimpleIcon,
  'phone': PhoneIcon,
  'phone-incoming': PhoneIncomingIcon,
  'phone-outgoing': PhoneOutgoingIcon,
  'play': PlayIcon,
  'plus': PlusIcon,
  'printer': PrinterIcon,
  'prohibit': ProhibitIcon,
  'pulse': PulseIcon,
  'question': QuestionIcon,
  'rabbit': RabbitIcon,
  'record': RecordIcon,
  'rocket-launch': RocketLaunchIcon,
  'seal': SealIcon,
  'share-fat': ShareFatIcon,
  'sliders-horizontal': SlidersHorizontalIcon,
  'smiley': SmileyIcon,
  'smiley-angry': SmileyAngryIcon,
  'smiley-meh': SmileyMehIcon,
  'sneaker-move': SneakerMoveIcon,
  'sparkle': SparkleIcon,
  'speaker-simple-high': SpeakerSimpleHighIcon,
  'speedometer': SpeedometerIcon,
  'square': SquareIcon,
  'square-split-horizontal': SquareSplitHorizontalIcon,
  'squares-four': SquaresFourIcon,
  'stack-simple': StackSimpleIcon,
  'tag': TagIcon,
  'text-aa': TextAaIcon,
  'text-b': TextBIcon,
  'text-italic': TextItalicIcon,
  'text-strikethrough': TextStrikethroughIcon,
  'text-t': TextTIcon,
  'text-underline': TextUnderlineIcon,
  'thumbs-down': ThumbsDownIcon,
  'thumbs-up': ThumbsUpIcon,
  'timer': TimerIcon,
  'trash': TrashSimpleIcon,
  'tray': TrayIcon,
  'trend-down': TrendDownIcon,
  'trend-up': TrendUpIcon,
  'triangle': TriangleIcon,
  'trophy': TrophyIcon,
  'user-check': UserCheckIcon,
  'user-circle': UserCircleIcon,
  'user-circle-check': UserCircleCheckIcon,
  'user-rectangle': UserRectangleIcon,
  'user-sound': UserSoundIcon,
  'user-switch': UserSwitchIcon,
  'users': UsersIcon,
  'vinyl-record': VinylRecordIcon,
  'warning-circle': WarningCircleIcon,
  'warning-diamond': WarningDiamondIcon,
  'x': XIcon,
  'x-circle': XCircleIcon,
}
